import { Grid } from '@material-ui/core';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { CheckoutAreaProps } from 'src/checkout-area/checkout-area';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { SAVE_TIME_OUT, VOID_TIME_OUT } from '../common/helpers/config';
import { IElectronicJournalRecord } from '../common/interfaces/states/IElectronicJournalRecord';
import { AppState } from '../common/redux/configure-store';
import { ItemListActions } from '../common/redux/reducers/item-list-reducer';
import { trainingModeStyles } from '../common/training-mode/training-mode-styles';
import EmptyCart from './components/empty-cart';
import { styles } from './components/item-list-styles';
import ReturnModeWatermark from './components/return-mode-watermark';
import TaxExemptWatermark from './components/tax-exempt-watermark';
import SaveTransaction from './components/save-transaction';
import TransactionVoided from './components/transaction-voided';
import { Pagination } from 'src/pagination/pagination';
import ItemListContainer from './components/item-list-container';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import GeneratedItemList from 'src/item-list/components/generated-item-list';
import { VERTICAL } from '@ncr-retail/hooks/build/useScrollButtonState/useScrollButtonState';

export const MAX_TENDER_ELIGIBILITY_RUNNING_TOTAL_LINES = 4;

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & CheckoutAreaProps;

const ItemList: React.FC<Props> = ({
  itemListState,
  setTransactionSaved,
  trainingModeActive,
  setTransactionVoided,
  returnModeActive,
  isCustomerDisplay,
}): JSX.Element => {
  const trainingModeClasses = trainingModeStyles();
  const [hasItems, setHasItems] = useState<boolean>(false);
  const [ejListSize, setEjListSize] = useState<number>(10);

  const isTBRActive = useSelector(
    (state: AppState): boolean =>
      state.posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn,
  );
  const transactionTaxExemptApplied = useSelector(
    (state: AppState): boolean => !!state.transaction.transactionTaxExemptApplied,
  );

  const tbrItems = useSelector(
    (state: AppState): IElectronicJournalRecord[] => state.tbr.tbrItemsList,
  );
  const itemsList = isTBRActive && !isCustomerDisplay ? tbrItems : itemListState.ejItems;
  const [
    { currentPage, lastPage, onPageChanged, navScrollDownEnabled, navScrollUpEnabled },
    scrollRef,
  ] = useScrollButtonState(itemsList.length, true, VERTICAL, ejListSize);

  const hasProductItem = itemsList.filter(i => i.Type === EJRecordTypes.Product).length > 0;
  const hasReturnItem = itemsList.filter(i => i.Type === EJRecordTypes.ReturnItem).length > 0;
  const items = itemsList.filter(({ pinned }) => !pinned);

  let pinnedItems = itemsList.filter(({ pinned }) => pinned);
  const tenderEligibilityItems = pinnedItems
    .filter(i => i.Type === EJRecordTypes.TenderEligibility)
    .slice(0, MAX_TENDER_ELIGIBILITY_RUNNING_TOTAL_LINES);
  pinnedItems = pinnedItems.filter(i => i.Type !== EJRecordTypes.TenderEligibility);
  const isTenderEligibleItems = !!tenderEligibilityItems.length;
  const classes = styles();

  const updateEjListSize = useCallback(() => {
    const height = window.innerHeight;
    let ejListSize = lastPage <= 1 ? 11 : 10;

    if (height < 600) {
      ejListSize = lastPage <= 1 ? 8 : 7;
    } else if (height < 740) {
      ejListSize = lastPage <= 1 ? 10 : 9;
    } else if (height < 800) {
      ejListSize = lastPage <= 1 ? 10 : 9;
    }

    setEjListSize(ejListSize);
  }, [lastPage]);

  useEffect(() => {
    if (window) {
      updateEjListSize();
      window.onresize = () => {
        updateEjListSize();
      };
    }
  }, [updateEjListSize]);

  useEffect((): void => {
    setHasItems(itemsList.length > 0);
  }, [itemsList]);

  useEffect((): void => {
    if (itemListState.transactionSaved) {
      setTimeout(() => setTransactionSaved(false), SAVE_TIME_OUT);
    }
  }, [itemListState.transactionSaved, setTransactionSaved]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined = undefined;
    if (itemListState.transactionVoided) {
      timeoutId = setTimeout(() => setTransactionVoided(false), VOID_TIME_OUT);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [itemListState.transactionVoided, setTransactionVoided]);

  const renderItemsListView = (): JSX.Element => {
    if (itemListState.transactionSaved) {
      return <SaveTransaction />;
    } else if (itemListState.transactionVoided) {
      return <TransactionVoided />;
    } else {
      return hasItems ? (
        <GeneratedItemList
          items={items}
          pageSize={ejListSize - pinnedItems.length - tenderEligibilityItems.length}
          tbrItemsList={tbrItems}
          isCustomerDisplay={isCustomerDisplay}
          trainingModeActive={trainingModeActive}
          isTBRActive={isTBRActive}
          isItems={true}
        />
      ) : (
        <EmptyCart />
      );
    }
  };

  const watermarkLength = returnModeActive || transactionTaxExemptApplied ? 1.15 : 0;

  return (
    <Grid container className={`fullHeight`} direction='column'>
      <Grid
        item
        container
        className={`fullWidth ${classes.container} ${classes.containerItem} ${
          classes.maincontainerItem
        } ${!hasItems ? classes.emptyFlexContainer : classes.notEmptyAlignContainer} ${
          trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
        } ${
          isCustomerDisplay
            ? classes.itemListContainerMaxHeightCustomerDisplay
            : classes.itemListContainerMaxHeight
        }`}
      >
        <div className='fullWidth fullHeight' style={{ marginBottom: lastPage <= 1 ? '1vh' : '' }}>
          {returnModeActive && (
            <Grid
              className={
                isCustomerDisplay
                  ? classes.returnModeWatermarkCustomer
                  : classes.returnModeWatermark
              }
            >
              <ReturnModeWatermark />
            </Grid>
          )}
          {transactionTaxExemptApplied && (
            <Grid
              className={
                isCustomerDisplay
                  ? classes.returnModeWatermarkCustomer
                  : classes.returnModeWatermark
              }
            >
              <TaxExemptWatermark />
            </Grid>
          )}
          <ItemListContainer
            ejListSize={ejListSize}
            pageSize={
              ejListSize - pinnedItems.length - tenderEligibilityItems.length - watermarkLength
            }
            isCustomerDisplay={isCustomerDisplay}
            items={true}
            pinnedAndTender={isTenderEligibleItems}
            scrollRef={scrollRef}
          >
            {renderItemsListView()}
          </ItemListContainer>
          {pinnedItems.length > 0 && (
            <ItemListContainer
              ejListSize={ejListSize}
              pageSize={pinnedItems.length}
              isCustomerDisplay={isCustomerDisplay}
              pinnedItems={true}
              isTenderEligibleItems={isTenderEligibleItems}
            >
              <GeneratedItemList
                items={pinnedItems}
                pageSize={pinnedItems.length}
                tbrItemsList={tbrItems}
                isCustomerDisplay={isCustomerDisplay}
                trainingModeActive={trainingModeActive}
                isTBRActive={isTBRActive}
              />
            </ItemListContainer>
          )}
          {tenderEligibilityItems.length > 0 && (
            <ItemListContainer
              ejListSize={ejListSize}
              pageSize={tenderEligibilityItems.length}
              isCustomerDisplay={isCustomerDisplay}
              tenderEligibleItems={true}
              isTenderEligibleItems={isTenderEligibleItems}
            >
              <GeneratedItemList
                items={tenderEligibilityItems}
                pageSize={tenderEligibilityItems.length}
                tbrItemsList={tbrItems}
                isCustomerDisplay={isCustomerDisplay}
                trainingModeActive={trainingModeActive}
                isTBRActive={isTBRActive}
                isDisabled={true}
              />
            </ItemListContainer>
          )}
        </div>
      </Grid>
      {!isCustomerDisplay && (
        <Grid
          item
          container
          className={`fullWidth ${classes.container} ${classes.navigationContainer} 
          ${
            hasProductItem && hasReturnItem
              ? classes.saleModeNavigationContainer
              : classes.maincontainerNavigationItem
          } ${trainingModeActive ? trainingModeClasses.trainingModeBackground : ''}`}
        >
          <Grid container justifyContent='space-evenly' className={classes.customContainerWidth}>
            {hasItems && (
              <Pagination
                currentPage={currentPage}
                totalPages={lastPage}
                updateCurrentPageHandler={onPageChanged}
                displayText={false}
                testIdPrefix={'item-list'}
                canScrollUp={navScrollUpEnabled}
                canScrollDown={navScrollDownEnabled}
                useAlternateArrowDesign={true}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const mapStateToProps = (state: AppState) => ({
  itemListState: state.itemList,
  trainingModeActive: state.posMode.trainingMode,
  returnModeActive: !!state.posMode.returnMode.returnType,
});

const mapDispatchToProps = {
  setTransactionSaved: ItemListActions.setTransactionSaved,
  setTransactionVoided: ItemListActions.setTransactionVoided,
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemList);
